import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/9e8b8adb-4702-43d4-a386-2eaf3df07374/images/da0a533c61bcb74f4223c68f8e3815e9368637c76e2d094c3f0f46931355b7f7.png";

const faqCategories = [
  {
    category: 'Booking & Reservations',
    questions: [
      {
        q: 'How do I book a ride with Toronto Royale?',
        a: 'You can request a booking through our website by filling out the form on our Contact page or Chauffeur Booking page. You can also call us directly at +1 (437) 996-3801. We recommend booking at least 24 hours in advance to ensure availability.',
      },
      {
        q: 'How far in advance should I book?',
        a: 'We recommend booking at least 24 hours in advance for standard services. For special occasions, events, or peak travel periods, we suggest booking 48\u201372 hours ahead. Last-minute requests are accommodated whenever possible.',
      },
      {
        q: 'How will I know my booking is confirmed?',
        a: 'Once we receive your booking request, we\u2019ll review availability and confirm via email, SMS, or phone \u2014 typically within 1 hour during business hours. You\u2019ll receive a booking confirmation with your chauffeur details and pickup information.',
      },
      {
        q: 'Can I modify or cancel my booking?',
        a: 'Yes. Cancellations made more than 12 hours before pickup incur no charge. Cancellations within 12 hours may be subject to a fee of up to 50% of the quoted fare. Modifications should be communicated as early as possible and are subject to availability.',
      },
    ],
  },
  {
    category: 'Services & Coverage',
    questions: [
      {
        q: 'What areas do you serve?',
        a: 'We primarily serve the Greater Toronto Area, including Toronto, Mississauga, Brampton, Vaughan, Markham, Richmond Hill, Oakville, and surrounding regions. We also offer long-distance routes to destinations like Niagara Falls, Waterloo, Kingston, and cross-border travel to the United States.',
      },
      {
        q: 'What types of services do you offer?',
        a: 'We offer executive travel, airport transfers (Pearson and Billy Bishop), family chauffeur services, special occasion transport, corporate travel, long-distance routes, and cross-border chauffeur services between Canada and the United States.',
      },
      {
        q: 'Do you offer airport pickup and drop-off?',
        a: 'Yes. We provide seamless airport transfers to and from Toronto Pearson International Airport and Billy Bishop Airport. Our service includes real-time flight tracking, meet-and-greet, and luggage assistance.',
      },
      {
        q: 'Do you provide cross-border transportation?',
        a: 'Yes. We offer premium cross-border chauffeur service between Canada and the United States. Our chauffeurs manage timing, documentation checkpoints, and route planning to ensure a stress-free border crossing experience.',
      },
    ],
  },
  {
    category: 'Fleet & Vehicles',
    questions: [
      {
        q: 'What vehicles are in your fleet?',
        a: 'Our curated fleet includes the Cadillac Escalade ESV, Mercedes-Benz S-Class, and Chevrolet Suburban Premier. Each vehicle is maintained to the highest standards for comfort, safety, and presence.',
      },
      {
        q: 'Can I request a specific vehicle?',
        a: 'Absolutely. You can indicate your vehicle preference when booking. We\u2019ll do our best to accommodate your request based on availability.',
      },
      {
        q: 'Are child safety seats available?',
        a: 'Yes. Please request a child safety seat at the time of booking, and we\u2019ll ensure one is installed and ready for your journey.',
      },
    ],
  },
  {
    category: 'Pricing & Payment',
    questions: [
      {
        q: 'How is pricing determined?',
        a: 'Pricing is based on the type of service, distance, duration, and vehicle selected. We provide transparent, upfront quotes before confirming your booking \u2014 no hidden fees or surprises.',
      },
      {
        q: 'What payment methods do you accept?',
        a: 'We accept major credit cards and electronic payments. Payment is due upon completion of the service or as agreed at the time of booking.',
      },
      {
        q: 'Are there additional charges I should be aware of?',
        a: 'Additional charges may apply for waiting time exceeding 15 minutes, toll fees, parking fees, and any extra stops not included in the original booking. These will always be communicated transparently.',
      },
    ],
  },
  {
    category: 'Experience & Safety',
    questions: [
      {
        q: 'Will I have the same driver each time?',
        a: 'That\u2019s our goal. As a boutique, founder-led service, we prioritize consistency. We aim to pair you with the same trusted chauffeur so you can build a relationship based on familiarity and trust.',
      },
      {
        q: 'What safety measures do you have in place?',
        a: 'All our vehicles carry comprehensive commercial insurance as required by Ontario law. Our chauffeurs are fully vetted, professionally trained, and committed to the highest safety and driving standards. Vehicles are regularly maintained and inspected.',
      },
      {
        q: 'Is my privacy respected during rides?',
        a: 'Absolutely. Discretion is one of our core values. Your conversations, schedule, and personal details are always kept confidential. We treat every client\u2019s privacy with the utmost respect.',
      },
      {
        q: 'What if I leave something in the vehicle?',
        a: 'Please contact us as soon as possible. We hold personal belongings for a reasonable period and will arrange a convenient return. Call us at +1 (437) 996-3801.',
      },
    ],
  },
];

const FAQItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);

  return (
    <div
      data-testid="faq-item"
      style={{
        borderBottom: '1px solid rgba(194,168,120,0.06)',
        transition: 'border-color 400ms ease',
      }}
    >
      <button
        data-testid="faq-toggle"
        onClick={() => setOpen(!open)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '28px 0', backgroundColor: 'transparent', border: 'none',
          cursor: 'pointer', textAlign: 'left', gap: '20px',
        }}
      >
        <span className="font-serif" style={{
          fontSize: '17px', fontWeight: 400, color: open ? '#F5F5F5' : 'rgba(245,245,245,0.75)',
          lineHeight: 1.4, transition: 'color 300ms ease',
        }}>
          {question}
        </span>
        <div style={{
          width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, transition: 'transform 400ms ease',
          transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
        }}>
          <ChevronDown size={18} strokeWidth={1.5} style={{ color: '#C2A878', opacity: 0.6 }} />
        </div>
      </button>
      <div style={{
        maxHeight: open ? '400px' : '0', overflow: 'hidden',
        transition: 'max-height 500ms ease, opacity 400ms ease',
        opacity: open ? 1 : 0,
      }}>
        <p style={{
          fontSize: '14px', color: 'rgba(225,225,225,0.8)', fontWeight: 300,
          lineHeight: 2, paddingBottom: '28px', maxWidth: '640px',
        }}>
          {answer}
        </p>
      </div>
    </div>
  );
};

export const FAQ = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('fade-in'); }),
      { threshold: 0.05, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.fade-element').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div data-testid="faq-page" style={{ backgroundColor: '#0A0A0A' }}>

      {/* ── HERO ── */}
      <section data-testid="faq-hero" className="relative overflow-hidden flex items-center justify-center" style={{ minHeight: '70vh' }}>
        <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: `url(${HERO_IMG})`,
          filter: 'brightness(0.35) saturate(0.8) contrast(1.08)',
          scale: '1.05',
          animation: 'heroZoom 25s ease-in-out infinite alternate',
        }} />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(180deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.2) 35%, rgba(0,0,0,0.15) 55%, rgba(0,0,0,0.75) 100%)',
        }} />
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 50% 35%, rgba(194,168,120,0.06) 0%, transparent 55%)',
        }} />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(135deg, rgba(0,0,0,0.3) 0%, transparent 40%, transparent 60%, rgba(0,0,0,0.3) 100%)',
        }} />
        <div className="grain-overlay absolute inset-0" style={{ opacity: 0.04 }} />

        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto" style={{ paddingTop: '160px', paddingBottom: '140px' }}>
          <div className="fade-element" style={{ animationDelay: '0.2s' }}>
            <div className="gold-divider mx-auto" style={{ marginBottom: '28px', opacity: 0.7 }} />
            <p style={{ fontSize: '11px', color: 'rgba(194,168,120,0.55)', letterSpacing: '5px', textTransform: 'uppercase', fontWeight: 300, marginBottom: '32px' }}>
              Have Questions?
            </p>
          </div>
          <div className="fade-element" style={{ animationDelay: '0.4s' }}>
            <h1 data-testid="faq-hero-heading" className="font-serif" style={{
              fontSize: 'clamp(38px, 5.5vw, 64px)', fontWeight: 500, color: '#FAFAFA',
              marginBottom: '28px', lineHeight: 1.08, textShadow: '0 2px 40px rgba(0,0,0,0.5)',
            }}>
              Frequently Asked Questions
            </h1>
          </div>
          <div className="fade-element" style={{ animationDelay: '0.6s' }}>
            <p style={{ fontSize: '17px', color: 'rgba(220,220,220,0.6)', fontWeight: 300, lineHeight: 1.8, maxWidth: '480px', margin: '0 auto' }}>
              Everything you need to know about our services, booking, and what to expect.
            </p>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0" style={{ height: '140px', background: 'linear-gradient(to top, #0A0A0A, transparent)' }} />
      </section>

      {/* ── FAQ CONTENT ── */}
      <section className="relative" style={{ backgroundColor: '#0A0A0A', padding: '4rem 1.5rem 8rem' }}>
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 30% 30%, rgba(194,168,120,0.02) 0%, transparent 50%)',
        }} />
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 70% 70%, rgba(194,168,120,0.015) 0%, transparent 50%)',
        }} />

        <div className="max-w-3xl mx-auto relative z-10">
          {faqCategories.map((cat, ci) => (
            <div key={cat.category} className="fade-element" style={{ marginBottom: ci < faqCategories.length - 1 ? '64px' : '0' }}>
              {/* Category Header */}
              <div style={{ marginBottom: '12px' }}>
                <div className="flex items-center" style={{ gap: '16px', marginBottom: '8px' }}>
                  <div style={{ width: '28px', height: '1px', backgroundColor: 'rgba(194,168,120,0.25)' }} />
                  <span style={{
                    fontSize: '11px', color: 'rgba(194,168,120,0.5)', letterSpacing: '3px',
                    textTransform: 'uppercase', fontWeight: 300,
                  }}>
                    {cat.category}
                  </span>
                </div>
              </div>

              {/* Questions */}
              <div style={{
                backgroundColor: 'rgba(14,14,14,0.35)',
                backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
                border: '1px solid rgba(194,168,120,0.05)',
                padding: '0 clamp(24px, 4vw, 40px)',
                position: 'relative',
              }}>
                <div style={{
                  position: 'absolute', top: 0, left: '10%', right: '10%', height: '1px',
                  background: 'linear-gradient(90deg, transparent, rgba(194,168,120,0.08), transparent)',
                }} />
                {cat.questions.map((item) => (
                  <FAQItem key={item.q} question={item.q} answer={item.a} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA ── */}
      <section data-testid="faq-cta" className="relative overflow-hidden" style={{ minHeight: '500px' }}>
        <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: `url(${HERO_IMG})`,
          filter: 'brightness(0.15) saturate(0.5)',
        }} />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(180deg, rgba(10,10,10,0.97) 0%, rgba(10,10,10,0.55) 35%, rgba(10,10,10,0.55) 65%, rgba(10,10,10,0.97) 100%)',
        }} />
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 50% 50%, rgba(194,168,120,0.06) 0%, transparent 45%)',
        }} />
        <div className="grain-overlay absolute inset-0" style={{ opacity: 0.04 }} />

        <div className="max-w-3xl mx-auto text-center relative z-10" style={{ padding: '10rem 1.5rem' }}>
          <div className="fade-element">
            <div className="gold-divider mx-auto" style={{ marginBottom: '28px', opacity: 0.6 }} />
            <h2 className="font-serif" style={{
              fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 500, color: '#FAFAFA',
              marginBottom: '20px', lineHeight: 1.15, textShadow: '0 2px 30px rgba(0,0,0,0.4)',
            }}>
              Still Have Questions?
            </h2>
            <p style={{
              fontSize: '16px', color: 'rgba(220,220,220,0.5)', fontWeight: 300,
              lineHeight: 1.8, maxWidth: '440px', margin: '0 auto 44px',
            }}>
              We're here to help. Reach out directly and we'll get back to you promptly.
            </p>
            <div className="flex flex-wrap justify-center" style={{ gap: '16px' }}>
              <button
                data-testid="faq-contact-btn"
                onClick={() => navigate('/contact')}
                style={{
                  backgroundColor: '#C2A878', color: '#0A0A0A', padding: '15px 40px',
                  fontSize: '12px', fontWeight: 500, letterSpacing: '1.2px', textTransform: 'uppercase',
                  border: 'none', cursor: 'pointer', transition: 'all 400ms ease',
                  boxShadow: '0 4px 24px rgba(194,168,120,0.15)',
                }}
                onMouseEnter={(e) => { e.target.style.backgroundColor = '#D4BA8A'; e.target.style.boxShadow = '0 6px 32px rgba(194,168,120,0.25)'; }}
                onMouseLeave={(e) => { e.target.style.backgroundColor = '#C2A878'; e.target.style.boxShadow = '0 4px 24px rgba(194,168,120,0.15)'; }}
              >
                Get in Touch
              </button>
              <a
                href="tel:+14379963801"
                data-testid="faq-call-btn"
                style={{
                  display: 'inline-flex', alignItems: 'center',
                  backgroundColor: 'transparent', color: '#C2A878', padding: '15px 40px',
                  fontSize: '12px', fontWeight: 400, letterSpacing: '1.2px', textTransform: 'uppercase',
                  border: '1px solid rgba(194,168,120,0.3)', textDecoration: 'none',
                  cursor: 'pointer', transition: 'all 400ms ease',
                }}
                onMouseEnter={(e) => { e.target.style.backgroundColor = 'rgba(194,168,120,0.08)'; e.target.style.borderColor = 'rgba(194,168,120,0.5)'; }}
                onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; e.target.style.borderColor = 'rgba(194,168,120,0.3)'; }}
              >
                Call +1 (437) 996-3801
              </a>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes heroZoom {
          0% { transform: scale(1.05); }
          100% { transform: scale(1.12); }
        }
      `}</style>
    </div>
  );
};
